import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import _ from 'lodash';
import { Alert, AlertTitle, GridLegacy } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid';
import { gridSpacing } from 'constants/themeConstants';
import { useGetDocumentExtractorsQuery } from 'store/slices/apiV1/inventory';
import { useGetOrgSuppliersServerSideQuery } from 'store/slices/apiV1/suppliers';
import ExtendedDatagrid from 'ui-component/DataGrid/ExtendedDatagrid';
import { DocumentExtractor, ExtractorSchemaType } from 'types/inventory';
import { DatagridNames, ExtendedGridColDef } from 'types/datagrid';
import { OrgSupplier } from 'types/suppliers';
import { longTextWithEllipsisRenderCell } from 'ui-component/DataGrid/Render';

const makeColumns = (suppliers: OrgSupplier[]): ExtendedGridColDef[] => [
  {
    field: 'name',
    headerName: 'Name',
    description: 'Extractor name',
    type: 'string',
    flex: 1,
  },
  {
    field: 'externalNotes',
    headerName: 'Notes',
    description: 'Notes',
    type: 'string',
    flex: 1,
  },
  {
    field: 'matchToSource',
    headerName: 'Source',
    description: 'The document source that this extractor will be used for',
    type: 'string',
    flex: 1,
  },
  {
    field: 'documentType',
    headerName: 'Document Type',
    description: 'The document type that this extractor will be used for',
    type: 'string',
    flex: 1,
  },
  {
    field: 'ocrType',
    headerName: 'OCR Type',
    description: 'The OCR type that this extractor will use',
    type: 'string',
    flex: 1,
  },
  {
    field: 'matchSubjectRegex',
    headerName: 'Subject Regex',
    description: 'The subject regex that this extractor will use',
    type: 'string',
    flex: 1,
  },
  {
    field: 'matchBodyRegex',
    headerName: 'Body Regex',
    description: 'The body regex that this extractor will use',
    type: 'string',
    flex: 1,
  },
  {
    field: 'matchDocumentRegex',
    headerName: 'Document Regex',
    description: 'The document regex that this extractor will use',
    type: 'string',
    flex: 1,
  },
  {
    field: 'matchSenderRegex',
    headerName: 'Sender Regex',
    description: 'The sender regex that this extractor will use',
    type: 'string',
    flex: 1,
  },
  {
    field: 'matchRecipientRegex',
    headerName: 'Recipient Regex',
    description: 'The recipient regex that this extractor will use',
    type: 'string',
    flex: 1,
  },
  {
    field: 'matchPriority',
    headerName: 'Match Priority',
    description: 'The match priority that this extractor will use',
    type: 'number',
    flex: 1,
  },
  {
    field: 'excludePageRegex',
    headerName: 'Exclude Page Regex',
    description: 'The exclude page regex that this extractor will use',
    type: 'string',
    flex: 1,
  },
  {
    field: 'ignoreAttachments',
    headerName: 'Ignore Attachments',
    description: 'Whether or not this extractor will ignore attachments',
    type: 'boolean',
    flex: 1,
  },
  {
    field: 'public',
    headerName: 'Public',
    description: 'Whether or not this extractor is public',
    type: 'boolean',
    flex: 1,
    valueGetter: (params: GridValueGetterParams) => !params.row.org,
  },
  {
    field: 'supplierObjectIds',
    headerName: 'Supplier(s)',
    description: 'Supplier(s) for this extractor',
    type: 'string',
    flex: 1,
    valueGetter: (params: GridValueGetterParams) =>
      params.row.default
        ? 'All Suppliers'
        : _.chain(params.row.supplierObjectIds)
            .map((oid) => {
              const supplier = _.find(suppliers, (s) => s.objectId === oid);
              return supplier ? supplier.name : '';
            })
            .join(', ')
            .value(),
    renderCell: longTextWithEllipsisRenderCell,
  },
];

const DocAiExtractorsTable = ({
  supplierObjectId,
}: {
  supplierObjectId?: string;
}) => {
  const {
    data: documentExtractors = [],
    isLoading: isLoadingDocumentExtractors,
  } = useGetDocumentExtractorsQuery();

  const filteredDocumentExtractors = _.filter(
    documentExtractors,
    (de) =>
      de.schemaType !== ExtractorSchemaType.PURCHASE_V1 &&
      !!de.active &&
      (!supplierObjectId ||
        de.default ||
        _.includes(de.supplierObjectIds, supplierObjectId))
  );

  const supplierIds = _.chain(filteredDocumentExtractors)
    .map('supplierObjectIds')
    .flatten()
    .uniq()
    .value();

  const { data: { data: suppliers = [] } = { data: [] } } =
    useGetOrgSuppliersServerSideQuery(
      supplierIds?.length !== 0
        ? {
            schema: ['id', 'name', 'objectId'],
            filters: [
              {
                field: 'objectId',
                operator: 'isAnyOf',
                value: supplierIds,
              },
            ],
          }
        : skipToken
    );

  const columns = useMemo<ExtendedGridColDef[]>(
    () => makeColumns(suppliers),
    [suppliers]
  );

  return (
    <GridLegacy container rowSpacing={gridSpacing}>
      <GridLegacy item xs={12}>
        <Alert severity="info">
          <AlertTitle>Contact Success</AlertTitle>
          To add or modify the Document Extractors for your organization contact
          Cofactr Success or your implementation team by emailing
          success@cofactr.com or using the in-platform chat in the lower right
          hand corner of this screen.
        </Alert>
      </GridLegacy>
      <GridLegacy item xs={12}>
        <ExtendedDatagrid<DocumentExtractor>
          loading={isLoadingDocumentExtractors}
          noRows="No Document Extractors found."
          gridName={DatagridNames.documentExtractors}
          rows={filteredDocumentExtractors ?? []}
          columns={columns}
          initialVisibleColumns={[
            'name',
            'supplierObjectIds',
            'matchToSource',
            'documentType',
            'public',
          ]}
        />
      </GridLegacy>
    </GridLegacy>
  );
};

export default DocAiExtractorsTable;
